"use client";

import Link from "next/link";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { Logo } from "@/components/ui/Logo";
import HeaderLocationDisplay from "@/components/layout/HeaderLocationDisplay";
import DesktopAccountMenu from "@/components/layout/header/DesktopAccountMenu";
import DesktopHeaderSearch from "@/components/layout/header/DesktopHeaderSearch";
import NotificationBell from "@/components/notifications/NotificationBell";
import NotificationDropdownPanel from "@/components/notifications/NotificationDropdownPanel";
import NotificationSetupPassiveChip from "@/components/notifications/NotificationSetupPassiveChip";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminProductsAttentionContext } from "@/contexts/AdminProductsAttentionContext";
import { useSellerListingAttentionContext } from "@/contexts/SellerListingAttentionContext";
import { useReferralMe } from "@/lib/hooks/useReferralMe";
import {
  MARKETPLACE_NAV,
  SELL_NAV,
  isMarketplaceActive,
  isSellActive,
  sellNavHref,
} from "@/lib/layout/site-nav";
import { cn } from "@/lib/utils";

type HeaderPanel = "account" | "notifications";

const DESKTOP_NOTIFICATIONS_PANEL_ID = "desktop-notification-dropdown";

export default function Header() {
  const pathname = usePathname() ?? "/";
  const { user, isAuthenticated, isLoading: isAuthLoading, logout } = useAuth();
  const { isPartner: referralNavPartner } = useReferralMe(
    isAuthenticated && !isAuthLoading,
  );
  const showInviteFriends =
    user?.partner_type === "market_partner" || referralNavPartner;
  const [openPanel, setOpenPanel] = useState<HeaderPanel | null>(null);
  const [authTimeout, setAuthTimeout] = useState(false);
  const notificationWrapRef = useRef<HTMLDivElement>(null);
  const {
    needsAttention: myItemsNeedsAttention,
    itemsNeedingAttentionCount: myItemsAttentionCount,
  } = useSellerListingAttentionContext();
  const { count: adminProductsAttentionCount } =
    useAdminProductsAttentionContext();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isAuthLoading) {
        console.warn(
          "Authentication is taking too long, showing login/signup options",
        );
        setAuthTimeout(true);
      }
    }, 5000);

    if (!isAuthLoading) {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isAuthLoading]);

  useEffect(() => {
    if (openPanel !== "notifications") return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!notificationWrapRef.current?.contains(event.target as Node)) {
        setOpenPanel(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenPanel(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openPanel]);

  const setAccountOpen = useCallback((open: boolean) => {
    setOpenPanel(open ? "account" : null);
  }, []);

  const handleLogout = async () => {
    setOpenPanel(null);
    await logout();
  };

  const showAuthenticatedContent = isAuthenticated && !isAuthLoading;
  const showLoginSignup = !isAuthenticated && (!isAuthLoading || authTimeout);
  const showAuthSpinner = isAuthLoading && !authTimeout;
  const marketplaceActive = isMarketplaceActive(pathname);
  const sellActive = isSellActive(pathname);

  return (
    <header className="mt-20 hidden bg-surface shadow-sm lg:block">
      <nav
        className="sio-glass rounded-sio-lg fixed top-0 right-0 left-0 z-[10010] m-4 mx-auto flex max-w-7xl items-center gap-4 p-4 lg:px-8"
        aria-label="Main"
      >
        <Link href="/" className="flex shrink-0 items-center">
          <span className="sr-only">Sell It Off</span>
          <Logo width={40} height={40} className="mr-2" />
          <span className="text-2xl font-bold text-primary">Sell It Off</span>
        </Link>

        <Suspense
          fallback={<div className="h-10 min-w-0 flex-1" aria-hidden="true" />}
        >
          <DesktopHeaderSearch />
        </Suspense>

        <div className="flex shrink-0 items-center gap-x-6">
          <Link
            href={MARKETPLACE_NAV.href}
            aria-current={marketplaceActive ? "page" : undefined}
            className={cn(
              "text-sm font-semibold leading-6 text-foreground hover:text-primary",
              marketplaceActive && "text-primary underline underline-offset-4",
            )}
            onClick={() => setOpenPanel(null)}
          >
            {MARKETPLACE_NAV.label}
          </Link>
          <Link
            href={sellNavHref(isAuthenticated)}
            aria-current={sellActive ? "page" : undefined}
            className={cn(
              "rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold leading-6 whitespace-nowrap text-white hover:bg-primary-600",
              sellActive && "ring-2 ring-primary ring-offset-2",
            )}
            onClick={() => setOpenPanel(null)}
          >
            {SELL_NAV.label}
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-x-4">
          {showAuthSpinner ? (
            <div className="flex items-center">
              <div className="mr-2 h-5 w-5 animate-spin rounded-full border-t-2 border-primary" />
              <span className="text-sm text-text-light">Loading...</span>
            </div>
          ) : (
            <div className="flex cursor-default items-center text-sm text-text-light">
              <MapPinIcon
                className="mr-1 h-4 w-4 text-primary"
                aria-hidden="true"
              />
              <HeaderLocationDisplay prefix="Near " />
            </div>
          )}

          {showAuthenticatedContent ? (
            <div className="flex items-center gap-2">
              <NotificationSetupPassiveChip />
              <div className="relative z-[10020]" ref={notificationWrapRef}>
                <NotificationBell
                  mode="dropdown"
                  ariaExpanded={openPanel === "notifications"}
                  ariaControls={DESKTOP_NOTIFICATIONS_PANEL_ID}
                  onClick={() =>
                    setOpenPanel((current) =>
                      current === "notifications" ? null : "notifications",
                    )
                  }
                />
                {openPanel === "notifications" ? (
                  <NotificationDropdownPanel
                    panelId={DESKTOP_NOTIFICATIONS_PANEL_ID}
                    userId={user?.id}
                    placement="anchored-right"
                    onClose={() => setOpenPanel(null)}
                  />
                ) : null}
              </div>
              <DesktopAccountMenu
                name={user?.name}
                avatarUrl={user?.avatar_url}
                isAdmin={Boolean(user?.isAdmin)}
                showInviteFriends={showInviteFriends}
                myItemsNeedsAttention={myItemsNeedsAttention}
                myItemsAttentionCount={myItemsAttentionCount}
                adminProductsAttentionCount={adminProductsAttentionCount}
                open={openPanel === "account"}
                onOpenChange={setAccountOpen}
                onLogout={handleLogout}
              />
            </div>
          ) : showLoginSignup ? (
            <>
              <Link
                href="/login"
                className="rounded-md border border-border px-4 py-2 text-sm font-semibold leading-6 text-foreground hover:border-border-strong"
              >
                Log in
              </Link>
              <Link
                href="/signup"
                className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold leading-6 text-white hover:bg-primary-600"
              >
                Sign up
              </Link>
            </>
          ) : null}
        </div>
      </nav>
    </header>
  );
}
